import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import { AudioBus } from '../../audio/AudioBus';
import { STORY_SOUNDS } from '../../logic/story/sounds';
import type { SceneLayoutContext } from '../../scenes/SceneComponent';
import { animate, ease } from '../board/index';
import { glowTexture } from '../road/glow';

/**
 * Where each torn piece of the treasure map sits on the whole map, in the
 * map art's pixels (assets/story/treasure_map.png is 1024 x 633; the pieces
 * were cut from it at that size, so each sits at its own x and y).
 */
export const MAP_ART = {
  w: 1024,
  h: 633,
  pieces: [
    { x: 0, y: 0, w: 374, h: 623 },
    { x: 343, y: 42, w: 348, h: 587 },
    { x: 662, y: 8, w: 362, h: 625 },
  ],
} as const;

/** The map's pieces laid out at `width`, each sliding in from beside its
 *  place as `t` goes 0 to 1 (1: the map is whole). */
export class MapPieces extends Container {
  private readonly sprites: Sprite[];
  private width_ = 1;

  constructor(pieces: (Texture | null)[]) {
    super();
    this.sprites = MAP_ART.pieces.map((_, i) => {
      const s = new Sprite(pieces[i] ?? undefined);
      s.label = `map-piece-${i + 1}`;
      this.addChild(s);
      return s;
    });
  }

  /** The map's height at `width`. */
  static heightAt(width: number): number {
    return Math.round((width * MAP_ART.h) / MAP_ART.w);
  }

  /** Where the X is, as a share of the map's width and height. */
  static readonly X_AT = { x: 0.79, y: 0.69 };

  layout(width: number): void {
    this.width_ = width;
    this.assemble(1);
  }

  /** `t` 0: each piece apart from its place, turned a little; 1: whole. */
  assemble(t: number): void {
    const k = this.width_ / MAP_ART.w;
    const spread = [-1, 0, 1];
    MAP_ART.pieces.forEach((p, i) => {
      const s = this.sprites[i]!;
      const e = 1 - t;
      s.scale.set(k);
      s.pivot.set(0, 0);
      s.rotation = spread[i]! * 0.12 * e;
      s.position.set(p.x * k + spread[i]! * 60 * e, p.y * k + (i === 1 ? -40 : 30) * e);
      s.alpha = Math.min(1, 0.2 + t * 1.2);
    });
  }
}

/**
 * A piece of the treasure map turning up on the reels: it pops up over the
 * middle of the reels with a glow and a rustle, hangs there a moment, then
 * flies to the map chip on the reel frame.
 */
export class MapPieceReveal extends Container {
  /** A light dim over the reels, so the piece stands out. */
  private readonly dim = new Graphics();
  private readonly glow = new Sprite();
  private readonly piece = new Sprite();
  private ctx: SceneLayoutContext | null = null;

  constructor(private readonly pieces: (Texture | null)[]) {
    super();
    this.glow.anchor.set(0.5);
    this.piece.anchor.set(0.5);
    this.piece.label = 'map-piece-found';
    this.addChild(this.dim, this.glow, this.piece);
    this.visible = false;
  }

  layout(ctx: SceneLayoutContext): void {
    this.ctx = ctx;
    const left = ctx.machineLeft ?? 0;
    const right = ctx.machineRight ?? ctx.width;
    this.dim.clear().rect(left, ctx.machineTop, right - left, ctx.machineBottom - ctx.machineTop).fill({ color: 0x0a0602, alpha: 0.45 });
  }

  /** Show piece `n` (1-based) and fly it to `to` (canvas pixels; null: it
   *  just fades). Resolves when it has gone. */
  async play(n: number, to: () => { x: number; y: number } | null): Promise<void> {
    const ctx = this.ctx;
    const tex = this.pieces[Math.max(0, Math.min(this.pieces.length - 1, n - 1))];
    if (!ctx || !tex) return;
    const left = ctx.machineLeft ?? 0;
    const right = ctx.machineRight ?? ctx.width;
    const x = (left + right) / 2;
    const y = (ctx.machineTop + ctx.machineBottom) / 2;
    const size = Math.min(ctx.machineBottom - ctx.machineTop - 20, (right - left) * 0.5, 260);
    const scale = size / Math.max(tex.width, tex.height);
    this.piece.texture = tex;
    this.glow.texture = glowTexture();
    this.glow.tint = 0xffd36a;
    this.glow.width = this.glow.height = size * 1.8;
    this.glow.position.set(x, y);
    this.piece.position.set(x, y);
    this.visible = true;
    this.alpha = 1;
    AudioBus.shared.play(STORY_SOUNDS.mapPiece, { volume: 0.85 });
    await animate(420, (t) => {
      const e = ease.outBack(t);
      this.piece.scale.set(scale * (0.3 + 0.7 * e));
      this.piece.rotation = (1 - t) * -0.5;
      this.glow.alpha = t;
      this.dim.alpha = t;
    });
    // A gentle sway while it hangs there.
    await animate(1100, (t) => {
      this.piece.rotation = Math.sin(t * Math.PI * 2) * 0.05;
    });
    const dest = to();
    const from = { x, y };
    await animate(520, (t) => {
      const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      this.glow.alpha = 1 - t;
      this.dim.alpha = 1 - t;
      if (dest) {
        this.piece.position.set(from.x + (dest.x - from.x) * e, from.y + (dest.y - from.y) * e - Math.sin(t * Math.PI) * 40);
        this.piece.scale.set(scale * (1 - 0.85 * e));
      } else {
        this.piece.alpha = 1 - t;
      }
    });
    this.visible = false;
    this.piece.alpha = 1;
  }
}

/** A soft golden glow behind the map's X, to show where to dig. */
export function xMarker(size: number): Sprite {
  const g = new Sprite(glowTexture());
  g.anchor.set(0.5);
  g.tint = 0xffd36a;
  g.width = g.height = size;
  g.label = 'map-x';
  return g;
}
